import { ReactElement, useLayoutEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import NumberFlow from '@number-flow/react';
import styled from '@emotion/styled';
import { BREAK_POINT, MEDIA_QUERY } from '@bucketplace/design-system';
import { GridItemProps } from '@bucketplace/design-system/bds';

import { cssColumn } from 'styles/grids';
import { Video } from './Video';

const EVENT_API_PATH = '/api/promotions';

interface DiscountTotalProps {
  promotionId: number;
  showDiscountTotal: boolean;
  startAt?: string;
}

interface DiscountTotalResponse {
  blackFridayTotalDiscountAmount: {
    pollingIntervalMillis: number,
    totalDiscountAmount: string,
  };
}

// 시간 단위
const DISPLAY_HOUR = 9;

// 시간을 밀리초 단위로 변환
const DISPLAY_TIME = DISPLAY_HOUR * 60 * 60 * 1000;

const fetcher = (url: string): Promise<DiscountTotalResponse> => fetch(url).then(res => res.json());

export function DiscountTotal({ promotionId, showDiscountTotal, startAt }: DiscountTotalProps): ReactElement {
  const [timeGap, setTimeGap] = useState(-1);
  const interval = useRef<number>();

  const { data: discountTotalData, error: discountTotalError } = useSWR<DiscountTotalResponse>(
    `${EVENT_API_PATH}/${promotionId}/discount-total`, fetcher, {
      refreshInterval: 1000,
      isPaused: () => !showDiscountTotal,
    }
  );

  useLayoutEffect(() => {
    if (!startAt) {
      return null;
    }

    const compareDisplayDateToDuration = (): void => {
      // 블랙프라이데이 시작 시간 + n시간
      const startTime = new Date(new Date(startAt).getTime() + DISPLAY_TIME);
      const currentTime = new Date();
      const diff = startTime.getTime() - currentTime.getTime();

      if (currentTime >= startTime || diff <= 0) {
        setTimeGap(0);
        clearInterval(interval.current);
      } else {
        setTimeGap(diff);
      }
    };

    compareDisplayDateToDuration();

    interval.current = window.setInterval(compareDisplayDateToDuration, 1000);

    return () => clearInterval(interval.current);
  }, [startAt]);

  return (
    <Container>
      <GridItem
        column={{
          mobile: 'full',
          tablet: 'full',
          laptop: 9,
        }}
      >
        <Video />
        { showDiscountTotal && !discountTotalError && timeGap <= 0 && (
          <DiscountContainer>
            <Title>블랙 프라이데이 누적 할인 금액</Title>
            <PriceContainer>
              <PriceText style={{ marginRight: '1.3px' }}>총</PriceText>
              <PriceWrapper>
                <NumberFlow
                  value={Number(discountTotalData?.blackFridayTotalDiscountAmount?.totalDiscountAmount ?? '0')}
                  spinTiming={{ duration: 300 }}
                />
              </PriceWrapper>
              <PriceText style={{ marginLeft: '1.3px' }}>원</PriceText>
            </PriceContainer>
            <Description>
              혜택 금액 유의 사항은 하단을 참조하세요.
            </Description>
          </DiscountContainer>
        ) }
      </GridItem>
    </Container>
  );
}

const Container = styled.section`
  display: flex;
  flex-wrap: wrap;
  max-width: ${BREAK_POINT.desktop}px;
  margin: 0 auto;
  padding: 0 60px;
  ${MEDIA_QUERY.max('tablet', `
    padding: 0;
  `)}
`;

const GridItem = styled.div<{ column: GridItemProps['column'] }>`
  position: relative;
  margin: 0 auto;
  ${({ column }) => cssColumn(column)};
`;

const DiscountContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 30px 12px;
  font-family: Pretendard;
  font-weight: 500;
  background-color: ${({ theme }) => theme.base.neutral700};
`;

const Title = styled.p`
  color: ${({ theme }) => theme.colors.background};
  font-size: 15px;
  font-weight: 500;
  line-height: 15px;
  margin-bottom: 4px;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 7px;
`;

const PriceText = styled.span`
  color: #1AA7FF;
  font-size: 26px;
  font-weight: 500;
  line-height: normal;
`;

const PriceWrapper = styled.span`
  display: flex;
  color: #1AA7FF;
  font-family: Quantico, sans-serif;
  font-weight: 400;
  font-size: 30px;
  line-height: 0px;
  letter-spacing: -1.2px;
`;

const Description = styled.p`
  color: #3D3D3D;
  text-align: center;
  font-size: 11.8px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;
